import { useState, useMemo } from 'react';
import { Lightbulb, Plus, Filter, ArrowUpDown, CheckCircle2, Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { StandardSheet } from '@/components/shared/StandardSheet';
import { ProposalCard, ProposalForm, ProposalDetail } from '@/components/proposals';
import {
  StandardProposal,
  useProposals,
  useProposalCommentCounts,
  useSubmitProposal,
} from '@/hooks/useProposals';

type FilterStatus = 'open' | 'closed' | 'all';
type SortBy = 'votes' | 'date';

export default function StandardsProposals() {
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<StandardProposal | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('open');
  const [sortBy, setSortBy] = useState<SortBy>('votes');

  // Map UI filter to actual statuses
  const statusFilter = useMemo(() => {
    if (filterStatus === 'open') return undefined; // We'll filter client-side
    if (filterStatus === 'closed') return undefined;
    return undefined;
  }, [filterStatus]);

  const { data: allProposals = [], isLoading } = useProposals({
    type: filterType !== 'all' ? filterType : undefined,
    status: statusFilter,
    sortBy,
  });

  // Filter proposals based on open/closed status
  const proposals = useMemo(() => {
    if (filterStatus === 'open') {
      return allProposals.filter((p) => 
        p.status === 'pending' || p.status === 'under_review'
      );
    }
    if (filterStatus === 'closed') {
      return allProposals.filter((p) => 
        p.status === 'approved' || p.status === 'rejected' || p.status === 'implemented'
      );
    }
    return allProposals;
  }, [allProposals, filterStatus]);

  const proposalIds = proposals.map((p) => p.id);
  const { data: commentCounts = {} } = useProposalCommentCounts(proposalIds);

  const openCount = allProposals.filter(
    (p) => p.status === 'pending' || p.status === 'under_review'
  ).length;
  const implementedCount = allProposals.filter(
    (p) => p.status === 'implemented'
  ).length;

  return (
    <Layout>
      <SEOHead path="/proposals" />

      <div className="container max-w-4xl py-6 md:py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Lightbulb className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Community Proposals
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Help shape the future of automotive data standards. Submit proposals
            for new fields, enum values, and standards. Vote on ideas you support.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Lightbulb className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{openCount}</div>
                <div className="text-sm text-muted-foreground">Open Proposals</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{implementedCount}</div>
                <div className="text-sm text-muted-foreground">Implemented</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Button onClick={() => setShowSubmitForm(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Submit Proposal
          </Button>

          <div className="flex flex-1 gap-2 flex-wrap sm:justify-end">
            <Tabs
              value={filterStatus}
              onValueChange={(v) => setFilterStatus(v as FilterStatus)}
            >
              <TabsList>
                <TabsTrigger value="open">Open</TabsTrigger>
                <TabsTrigger value="closed">Closed</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="field">Fields</SelectItem>
                <SelectItem value="enum_value">Enum Values</SelectItem>
                <SelectItem value="standard">Standards</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
              <SelectTrigger className="w-[130px]">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="votes">Most Votes</SelectItem>
                <SelectItem value="date">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Proposals List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <Skeleton className="h-12 w-12 rounded-lg" />
                    <div className="flex-1 space-y-3">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : proposals.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Lightbulb className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No proposals yet</h3>
              <p className="text-muted-foreground mb-4">
                Be the first to submit a proposal for the community to vote on.
              </p>
              <Button onClick={() => setShowSubmitForm(true)}>
                Submit a Proposal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {proposals.map((proposal) => (
              <ProposalCard
                key={proposal.id}
                proposal={proposal}
                commentCount={commentCounts[proposal.id] || 0}
                onViewDetails={() => setSelectedProposal(proposal)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Submit Proposal Sheet */}
      <StandardSheet
        open={showSubmitForm}
        onOpenChange={setShowSubmitForm}
        title="Submit a Proposal"
        description="Propose a new field, enum value, or standard for the community to vote on."
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowSubmitForm(false)}>
              Cancel
            </Button>
            <Button type="submit" form="proposal-form">
              Submit Proposal
            </Button>
          </div>
        }
      >
        <ProposalForm
          formId="proposal-form"
          onSuccess={() => setShowSubmitForm(false)}
          hideActions
        />
      </StandardSheet>

      {/* Proposal Detail Sheet */}
      <ProposalDetail
        proposal={selectedProposal}
        open={!!selectedProposal}
        onOpenChange={(open) => !open && setSelectedProposal(null)}
      />
    </Layout>
  );
}
